import React, { useState, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import styles from "./Button.module.css";
import PrivacyPolicy from '../privacy/Privacy';

const Button = (props) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  
  // State for validation errors
  const [errors, setErrors] = useState({
    fullName: '',
    phone: '',
    reason: ''
  });

  // State for form submission status
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // הוסף state עבור מדיניות הפרטיות
  const [agreed, setAgreed] = useState(false);

  // Refs for form fields
  const fullNameRef = useRef(null);
  const phoneRef = useRef(null);
  const reasonRef = useRef(null);

  // Server settings - התאם לפי הצורך
  const serverUrl = "https://dynamic-server-dfc88e1f1c54.herokuapp.com/leads/newLead";
  const zapierWebhookUrl = "https://hooks.zapier.com/hooks/catch/12851209/bjclnzb/";
  const reciver = "pedimenihanala@gmail.com"; // התאם למייל של רחליr

  const handleButtonClick = () => {
    setIsFormOpen(true);
    document.body.style.overflow = 'hidden'; // מונע גלילה ברקע
  };

  const closeForm = () => {
    setIsFormOpen(false);
    document.body.style.overflow = 'auto'; // מחזיר את הגלילה
    // איפוס השדות אם הטופס נסגר ללא שליחה
    if (!submitted) {
      if (fullNameRef.current) fullNameRef.current.value = "";
      if (phoneRef.current) phoneRef.current.value = "";
      if (reasonRef.current) reasonRef.current.value = "";
      setErrors({
        fullName: '',
        phone: '',
        reason: ''
      });
      setAgreed(false); // איפוס גם לתיבת האישור
    }
  };

  // הוסף פונקציה לטיפול בקליק על מדיניות הפרטיות
  const handlePrivacyClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const name = fullNameRef.current.value;
    const phone = phoneRef.current.value;
    const reason = reasonRef.current.value;
    
    // בדיקת אישור מדיניות הפרטיות
    if (!agreed) {
      alert("עליך לאשר את תנאי השימוש ומדיניות הפרטיות");
      return;
    }
    
    // Validate inputs
    let valid = true;
    const newErrors = { ...errors };

    // Validate full name
    if (!name.trim()) {
      newErrors.fullName = 'נא להזין שם מלא';
      valid = false;
    } else if (name.trim().length < 2) {
      newErrors.fullName = 'שם חייב להכיל לפחות 2 תווים';
      valid = false;
    } else {
      newErrors.fullName = '';
    }

    // Validate phone number (Israeli format)
    const phoneRegex = /^0(5\d|[23489])\d{7}$/;
    if (!phone.trim()) {
      newErrors.phone = 'נא להזין מספר טלפון';
      valid = false;
    } else if (!phoneRegex.test(phone.trim())) {
      newErrors.phone = 'נא להזין מספר טלפון תקין';
      valid = false;
    } else {
      newErrors.phone = '';
    }

    // Validate reason
    if (!reason.trim()) {
      newErrors.reason = 'נא להזין סיבת פנייה';
      valid = false;
    } else if (reason.trim().length < 5) {
      newErrors.reason = 'נא להזין לפחות 5 תווים';
      valid = false;
    } else {
      newErrors.reason = '';
    }

    setErrors(newErrors);
    
    if (!valid) {
      return;
    }
    
    setIsSubmitting(true);
    
    // Server data object (ללא email)
    const serverData = {
      name: name,
      phone: phone,
      reason: reason,
      reciver: reciver
    };

    // Zapier webhook - FormData עם encoding נקי
    const formData = new URLSearchParams();
    formData.append('firstName', name.trim());
    formData.append('lastName', '.');
    formData.append('cellPhone', phone.trim());
    formData.append('interest', 'קורס דיגיטלי מיקרובליידינג');
    formData.append('campaign', 'אתר רחלי לוי');
    formData.append('authorization', 'RoAuto LeadSource=K+OhgLhZTCxObekZWw3D4Xh6V+1VarEGMbsqvcB4+ZhFQCrDbcyyCfHSOY0+dVqU');
    
    // Debug logging
    console.log('Sending to Zapier - URLSearchParams entries:');
    for (const [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`);
    }

    try {
      // Send to both endpoints simultaneously
      console.log('=== DEBUG: Starting form submission ===');
      console.log('Name:', name);
      console.log('Phone:', phone);
      console.log('Reason:', reason);
      
      // Server data for original API
      console.log('Server data:', serverData);
      
      const [serverResponse, zapierResponse] = await Promise.allSettled([
        fetch(serverUrl, {
          method: "POST",
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(serverData)
        }),
        fetch(zapierWebhookUrl, {
          method: "POST",
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: formData.toString()
        })
      ]);
      
      console.log('=== Response Status ===');
      console.log('Server response status:', serverResponse.status);
      console.log('Zapier response status:', zapierResponse.status);
      
      if (zapierResponse.status === 'fulfilled') {
        console.log('Zapier response ok:', zapierResponse.value.ok);
        const zapierText = await zapierResponse.value.text();
        console.log('Zapier response text:', zapierText);
      } else {
        console.log('Zapier error:', zapierResponse.reason);
      }

      // Check if at least one submission was successful
      const serverSuccess = serverResponse.status === 'fulfilled' && serverResponse.value.ok;
      const zapierSuccess = zapierResponse.status === 'fulfilled' && zapierResponse.value.ok;

      if (serverSuccess || zapierSuccess) {
        setIsSubmitting(false);
        setSubmitted(true);
        
        // Log any failures for debugging
        if (!serverSuccess) {
          console.warn('Server submission failed:', serverResponse.reason || serverResponse.value);
        }
        if (!zapierSuccess) {
          console.warn('Zapier webhook failed:', zapierResponse.reason || zapierResponse.value);
        }
        
        // Reset form after successful submission
        setTimeout(() => {
          fullNameRef.current.value = "";
          phoneRef.current.value = "";
          reasonRef.current.value = "";
          setSubmitted(false);
          setErrors({
            fullName: '',
            phone: '',
            reason: ''
          });
          setAgreed(false); // איפוס גם לתיבת האישור
          closeForm();
        }, 3000);
      } else {
        throw new Error('Both submissions failed');
      }
    } catch (error) {
      alert("התרחשה שגיאה, אנא נסו שוב מאוחר יותר");
      console.error('Error submitting form:', error);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className={styles.center}>
        <button className={styles.button} onClick={handleButtonClick}>
          <span className={styles.buttonContent}>
            {props.text}
          </span>
          <div className={styles.shine}></div>
        </button>
      </div>

      {/* Form Overlay */}
      {isFormOpen && (
        <div className={styles.formOverlay} onClick={closeForm}>
          <div className={styles.formModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.formHeader}>
              <button className={styles.formCloseButton} onClick={closeForm}>
                <FaTimes />
              </button>
            </div>
            
            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="fullName">שם מלא</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  className={`${styles.input} ${errors.fullName ? styles.inputError : ''}`}
                  placeholder="השם המלא שלך"
                  disabled={isSubmitting || submitted}
                  ref={fullNameRef}
                />
                {errors.fullName && <p className={styles.errorText}>{errors.fullName}</p>}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="phone">מספר טלפון</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
                  placeholder="050-0000000"
                  disabled={isSubmitting || submitted}
                  ref={phoneRef}
                />
                {errors.phone && <p className={styles.errorText}>{errors.phone}</p>}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="reason">סיבת הפנייה</label>
                <textarea
                  id="reason"
                  name="reason"
                  rows="4"
                  className={`${styles.textarea} ${errors.reason ? styles.inputError : ''}`}
                  placeholder="סיבת הפנייה"
                  disabled={isSubmitting || submitted}
                  ref={reasonRef}
                />
                {errors.reason && <p className={styles.errorText}>{errors.reason}</p>}
              </div>
              
              {/* תיבת האישור למדיניות הפרטיות */}
              <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                marginTop: "20px",
                marginBottom: "20px"
              }}>
                <label style={{ 
                  direction: "rtl", 
                  fontFamily: "AssistantR", 
                  fontSize: "0.9rem", 
                  textAlign: "right", 
                  display: "flex", 
                  alignItems: "center", 
                  flexWrap: "wrap", 
                  gap: "5px" 
                }}>
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={() => setAgreed(!agreed)}
                    style={{ marginLeft: "10px" }}
                    disabled={isSubmitting || submitted}
                  />
                  קראתי את
                  <span onClick={handlePrivacyClick}>
                    <PrivacyPolicy 
                      ownerName="רחלי לוי" 
                      email="rachelilevy.beauty@gmail.com" 
                      domain="https://pedimeniracheli.co.il/" 
                    />
                  </span>
                  ואני מאשר/ת
                </label>
              </div>
              
              <button 
                type="submit" 
                className={`${styles.submitButton} ${isSubmitting ? styles.submitting : ''} ${submitted ? styles.submitted : ''}`}
                disabled={isSubmitting || submitted}
              >
                {isSubmitting ? 'שולח...' : submitted ? 'נשלח בהצלחה!' : 'רחלי, בואי נדבר!'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Button;